import {
    createSelector,
    createStructuredSelector,
} from 'reselect';
import {
    AbstractedTariffWithHardwareOffer,
    AbstractedTariffWithHardwareResponseItem,
    getDataVolumeWithOrWithoutDiscounts,
    getFromArrayWithRunTimeEnd,
    getMonthlyPriceWithOrWithoutDiscounts,
    isTariffWithHardwareResponse,
} from '@vfde-sails/glados-v2';
import { roundToTwoDecimal } from '@vfde-sails/utils';
import { RootState } from '../../store';
import {
    selectActiveSubscriptionId,
    selectAvailableSubscriptionIds,
    selectCustomerConfig,
    selectIsEligibleForRestlaufzeit,
    selectAtomicId,
} from 'Container/App/slice';
import {
    getSimOnly,
    getTariffWithHardware,
} from 'Container/App/api/glados';
import {
    RedTariff,
    YoungTariff,
} from '@vfde-sails/constants';

const createAppSelector = createSelector.withTypes<RootState>();

const createSimOnlySelector = createAppSelector(
    [
        selectCustomerConfig,
        selectIsEligibleForRestlaufzeit,
    ],
    ({ isRestlaufzeit }, isEligibleForRestlaufzeit) =>
        getSimOnly.select(isRestlaufzeit && isEligibleForRestlaufzeit),
);
const createTariffWithHardwareSelector = createAppSelector(
    [
        selectCustomerConfig,
        selectAtomicId,
        selectIsEligibleForRestlaufzeit,
    ],
    ({ isRestlaufzeit }, atomicId, isEligibleForRestlaufzeit) =>
        getTariffWithHardware.select({ atomicId: atomicId!, isRestlaufzeit: isEligibleForRestlaufzeit && isRestlaufzeit }),
);

const selectSimOnlyQuery = createAppSelector(
    [
        state => state,
        createSimOnlySelector,
    ],
    (state, selector) => selector(state),
);

const selectTariffWithHardwareQuery = createAppSelector(
    [
        state => state,
        createTariffWithHardwareSelector,
    ],
    (state, selector) => selector(state),
);

const selectLoading = createAppSelector(
    [
        selectSimOnlyQuery,
        selectTariffWithHardwareQuery,
    ],
    (simOnlyQuery, tariffWithHardwareQuery) => simOnlyQuery.isLoading || tariffWithHardwareQuery.isLoading,
);

const selectHasError =
    createAppSelector(
        [
            selectSimOnlyQuery,
            selectTariffWithHardwareQuery,
        ],
        (simOnlyQuery, tariffWithHardwareQuery) => simOnlyQuery.isError || tariffWithHardwareQuery.isError,
    );

const selectSimOnlyResponseData = createAppSelector(
    selectSimOnlyQuery,
    state => state.data || null,
);

const selectWithHardwareResponseData = createAppSelector(
    selectTariffWithHardwareQuery,
    state => state.data || null,
);

const selectData = createAppSelector(
    [
        selectCustomerConfig,
        selectSimOnlyResponseData,
        selectWithHardwareResponseData,
    ],
    (customerConfig, simOnlyData, withHardwareData) =>
        customerConfig.atomicId ? withHardwareData : simOnlyData,
);

const selectDataSet = createAppSelector(
    [
        selectCustomerConfig,
        selectData,
    ],
    (customerConfig, data) => {
        if (!data) {
            return null;
        }

        if (isTariffWithHardwareResponse(data)) {
            return data
                .find(set => set.hardware.atomicId === customerConfig.atomicId) || null;
        }

        return data;
    },
);

const selectAllOffers = createAppSelector(
    selectDataSet,
    data => data?.tariffs || [],
);

const selectAvailableOffers = createAppSelector(
    [
        selectAllOffers,
        selectAvailableSubscriptionIds,
    ],
    (offers, availableSubscriptionIds) => offers?.filter(subscription => availableSubscriptionIds.includes(subscription.tariffId)),
);

const selectCurrentOfferData = createAppSelector(
    [
        selectActiveSubscriptionId,
        selectAllOffers,
    ],
    (activeSubscriptionId, offers) =>
        offers?.find(offer => activeSubscriptionId === offer.tariffId) || null,
);

const selectDeviceData = createAppSelector(
    selectDataSet,
    data => (data as AbstractedTariffWithHardwareResponseItem<RedTariff | YoungTariff> | null)?.hardware || null,
);

const selectInsurancePrice = createAppSelector(
    [
        selectCustomerConfig,
        selectCurrentOfferData,
    ],
    ({ insurance }, activeOffer) => {
        if (!insurance || !activeOffer) {
            return null;
        }

        return (activeOffer as AbstractedTariffWithHardwareOffer<RedTariff | YoungTariff>).priceInsurance || null;
    },
);

const selectTariffPriceWithDiscounts = createAppSelector(
    selectCurrentOfferData,
    activeOffer => activeOffer?.priceMonthlyTariffWithDiscounts || null,
);

const selectTariffPriceWithoutDiscounts = createAppSelector(
    selectCurrentOfferData,
    activeOffer => activeOffer?.priceMonthlyTariffWithoutDiscounts || null,
);

const selectTariffPrice = createAppSelector(
    [
        selectTariffPriceWithDiscounts,
        selectTariffPriceWithoutDiscounts,
    ],
    (tariffPriceWithDiscounts, tariffPriceWithoutDiscounts) => {
        if (!tariffPriceWithoutDiscounts) {
            return null;
        }

        return getMonthlyPriceWithOrWithoutDiscounts(
            tariffPriceWithoutDiscounts,
            tariffPriceWithDiscounts,
        );
    },
);

const selectEndTariffPrice = createAppSelector(
    [
        selectTariffPriceWithDiscounts,
        selectTariffPriceWithoutDiscounts,
    ],
    (tariffPriceWithDiscounts, tariffPriceWithoutDiscounts) => {
        if (!tariffPriceWithoutDiscounts) {
            return null;
        }

        return getMonthlyPriceWithOrWithoutDiscounts(
            tariffPriceWithoutDiscounts,
            tariffPriceWithDiscounts,
            getFromArrayWithRunTimeEnd,
        );
    },
);

const selectDataVolume = createAppSelector(
    selectCurrentOfferData,
    activeOffer => {
        if (!activeOffer) {
            return null;
        }

        return getDataVolumeWithOrWithoutDiscounts(
            activeOffer.dataVolumeWithoutDiscounts,
            activeOffer.dataVolumeWithDiscounts,
        );
    },
);

const selectEndDataVolume = createAppSelector(
    selectCurrentOfferData,
    activeOffer => {
        if (!activeOffer) {
            return null;
        }

        return getDataVolumeWithOrWithoutDiscounts(
            activeOffer.dataVolumeWithoutDiscounts,
            activeOffer.dataVolumeWithDiscounts,
            getFromArrayWithRunTimeEnd,
        );
    },
);

const selectStairway = createAppSelector(
    selectTariffPrice, selectEndTariffPrice,
    (tariffPrice, endTariffPrice) =>
        !!tariffPrice && !!endTariffPrice && tariffPrice !== endTariffPrice,
);

const selectPriceToPay = createAppSelector(
    [
        selectTariffPrice,
        selectInsurancePrice,
    ],
    (tariffPrice, insurancePrice) => {
        if (!tariffPrice) {
            return null;
        }

        return {
            ...tariffPrice,
            gross: roundToTwoDecimal(tariffPrice.gross + (insurancePrice?.gross || 0)),
        };
    },
);

const selectEndPriceToPay = createAppSelector(
    [
        selectEndTariffPrice,
        selectInsurancePrice,
    ],
    (tariffPrice, insurancePrice) => {
        if (!tariffPrice) {
            return null;
        }

        return {
            ...tariffPrice,
            gross: roundToTwoDecimal(tariffPrice.gross + (insurancePrice?.gross || 0)),
        };
    },
);

const selectStrikePrice = createAppSelector(
    [
        selectCurrentOfferData,
        selectCustomerConfig,
    ],
    (activeOffer, customerConfig) => {
        if (!activeOffer) {
            return null;
        }

        return customerConfig.insurance
            // it seems that TypeScript cant infer the variable return type of `selectDataSet`, so we have to cast here
            ? (activeOffer as AbstractedTariffWithHardwareOffer).priceMonthlyStrikePriceWithInsurance
            : activeOffer.priceMonthlyStrikePrice
        ;
    },
);

const selectTrackingPayload = createAppSelector(
    [
        selectCurrentOfferData,
        selectDeviceData,
        selectCustomerConfig,
    ],
    (subscription, atomic, customerConfig) => ({
        subscription,
        atomic,
        customerConfig,
    }),
);

/**
 * Create typed structured selector
 */
export const createStructuredAppSelector =
    createStructuredSelector.withTypes<RootState>();

export {
    selectLoading,
    selectHasError,
    selectData,
    selectAvailableOffers,
    selectCurrentOfferData,
    selectDeviceData,
    selectTariffPrice,
    selectEndTariffPrice,
    selectDataVolume,
    selectEndDataVolume,
    selectInsurancePrice,
    selectStairway,
    selectStrikePrice,
    selectPriceToPay,
    selectEndPriceToPay,
    selectTrackingPayload,
};
